import { Alert, Form, Typography } from "antd";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useGetBanksOptions } from "../../../../../hooks/useGetBanksOptions";
import { formatCurrency, formatDateByType } from "../../../../libs/utils";
import { ButtonGroup } from "../../../../molecules/Button/ButtonGroup";
import { ReplacementChequeFields } from "./ReplacementChequeFields";

const { Text } = Typography;

type SelectedReceipt = {
  receiptNo: string | number;
  chequeNo: string | number;
  bankName: string;
  paidDate: string;
  amountToPay: number;
};

type ReplacementChequeFormProps = {
  selectedReceipt: SelectedReceipt;
  usageType: string;
  depositTo?: string;
  depositToBank?: string;
  recallFee?: number;
  onCancel?: () => void;
  onSubmit?: (params: {
    recallType: "replaceWithNewReceipt";
    recallReason?: string;
    waiveOff?: boolean;
    modeOfPayment?: string;
    amountToPay?: number;
    documentNo?: string;
    bankName?: string;
    paidDate?: string;
    depositDate?: string;
    depositTo?: string;
    depositToBank?: string;
  }) => Promise<void>;
  isPending?: boolean;
};

type FieldType = {
  paymentMode: { label: string; value: string } | null;
  amountToPay: number | null;
  paymentRef: string;
  bankName: { label: string; value: string } | null;
  paidDate: dayjs.Dayjs | null;
  depositDate: dayjs.Dayjs | null;
  depositTo: { label: string; value: string } | null;
  depositBank: { label: string; value: string } | null;
  recallReason: string;
  waiveFee: boolean;
};

export const ReplacementChequeForm = (props: ReplacementChequeFormProps) => {
  const { selectedReceipt, usageType, depositTo, depositToBank, recallFee = 0, onCancel, onSubmit, isPending } = props;

  const depositToOptions = useMemo(() => {
    if (!depositTo) return [];
    return [{ label: depositTo, value: depositTo }];
  }, [depositTo]);

  const { banksOptions } = useGetBanksOptions({ usageType });

  const methods = useForm<FieldType>({
    defaultValues: {
      paymentMode: null,
      amountToPay: null,
      paymentRef: "",
      bankName: null,
      paidDate: null,
      depositDate: null,
      depositTo: depositTo ? { label: depositTo, value: depositTo } : null,
      depositBank: depositToBank ? { label: depositToBank, value: depositToBank } : null,
      recallReason: "",
      waiveFee: false,
    },
  });

  const handleFormSubmit = methods.handleSubmit(async (data) => {
    await onSubmit?.({
      recallType: "replaceWithNewReceipt",
      recallReason: data.recallReason,
      waiveOff: data.waiveFee,
      modeOfPayment: data.paymentMode?.value,
      amountToPay: data.amountToPay,
      documentNo: data.paymentRef,
      bankName: data.bankName?.label,
      paidDate: formatDateByType(data.paidDate, "api"),
      depositDate: formatDateByType(data.depositDate, "api"),
      depositTo: data.depositTo?.value,
      depositToBank: data.depositBank?.label,
    });
  });

  const alertMessage = `Original receipt ${selectedReceipt.receiptNo} (cheque ${selectedReceipt.chequeNo}, ${formatCurrency(selectedReceipt.amountToPay)}) will be cancelled.`;

  return (
    <div>
      <Alert
        message={<span style={{ color: "var(--red-300)" }}>{alertMessage}</span>}
        type="error"
        showIcon={false}
        style={{ marginBottom: 20, background: "var(--red-100)", border: "none", borderRadius: 8 }}
      />

      <FormProvider {...methods}>
        <Form layout="vertical" onFinish={handleFormSubmit} className="bf-main">
          <ReplacementChequeFields banksOptions={banksOptions} depositToOptions={depositToOptions} />

          {recallFee > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f5f5f5",
                borderRadius: 6,
                padding: "10px 16px",
                marginBottom: 20,
              }}
            >
              <Text>Recall fee</Text>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Text strong>{formatCurrency(recallFee)}</Text>
              </div>
            </div>
          )}

          <ButtonGroup
            primaryButton={{ label: "Submit for approval", htmlType: "submit", loading: isPending }}
            secondaryButton={{ label: "Back", onClick: onCancel, disabled: isPending }}
          />
        </Form>
      </FormProvider>
    </div>
  );
};
